import { pluralCredits, type Pricing } from '@app/shared';
import type { Dictionary } from '../i18n';
import { ru } from '../i18n/ru';
import { ApiError } from './api-core';
import { errorText } from './errors';

/** Errors of saving a prompt version; anything else as on every other screen. */
export function adminErrorText(err: unknown, t: Dictionary): string {
  if (err instanceof ApiError) {
    const names = err.details.names;
    if (err.code === 'UNKNOWN_PLACEHOLDER' && Array.isArray(names) && names.length > 0) {
      return t.adminPrompts.unknownPlaceholder(names.map(String).join(', '));
    }
    if (err.code === 'INVALID_MODEL') return t.adminPrompts.invalidModel;
    if (err.code === 'EMPTY_BODY') return t.adminPrompts.emptyBody;
    const result = err.details.result;
    if (err.code === 'MODEL_CHECK' && typeof result === 'string' && Object.hasOwn(t.adminPrompts.check, result)) {
      return t.adminPrompts.check[result as keyof typeof t.adminPrompts.check];
    }
  }
  return errorText(err, t);
}

// Units have one language for now: the Russian dictionary.
const units = ru.units;

/** An estimate: «≈ 42 с», «≈ 1 мин 05 с». */
export function formatSeconds(s: number): string {
  const total = Math.round(s);
  if (total < 60) return units.approx(units.seconds(total));
  return units.approx(units.minutes(Math.floor(total / 60), String(total % 60).padStart(2, '0')));
}

/** A limit from the passport: «60 с». */
export function formatLimitSeconds(s: number): string {
  return units.seconds(s);
}

/** Money of the admin screens: «0,47 ¢» below ten cents, «$1,23» from ten cents. */
export function formatCents(usd: number): string {
  const comma = (n: number) => n.toFixed(2).replace('.', ',');
  if (usd < 0.1) return units.cents(comma(usd * 100));
  return units.dollars(comma(usd));
}

/** «30 сентября», in the person's own time zone. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

/** The Russian form of a count: [one, few, many] — «1 рилс», «2 рилса», «5 рилсов». */
export function pluralRu(n: number, forms: readonly [string, string, string]): string {
  const ten = Math.abs(n) % 10;
  const hundred = Math.abs(n) % 100;
  if (ten === 1 && hundred !== 11) return forms[0];
  if (ten >= 2 && ten <= 4 && (hundred < 12 || hundred > 14)) return forms[1];
  return forms[2];
}

/** «9 рилсов» */
export function countOf(n: number, forms: readonly [string, string, string]): string {
  return `${n} ${pluralRu(n, forms)}`;
}

/** «300 кредитов» */
export function creditsCount(n: number): string {
  return `${n} ${pluralCredits(n)}`;
}

export function priceLabel(n: number, freeWord: string): string {
  return n === 0 ? freeWord : creditsCount(n);
}

/** The price list in the order people meet the actions. */
export function priceRows(p: Pricing, t: Dictionary): Array<{ label: string; price: string }> {
  const free = t.credits.free;
  const rows = t.credits.priceRows;
  return [
    { label: rows.thought, price: priceLabel(p.thought, free) },
    { label: rows.moreIdeas, price: free },
    { label: rows.text, price: free },
    { label: rows.carouselStatic, price: priceLabel(p.carouselStatic, free) },
    { label: rows.carouselAnimated, price: priceLabel(p.carouselAnimated, free) },
    { label: rows.reel, price: priceLabel(p.reel, free) },
    { label: rows.reelDiagram, price: priceLabel(p.reelDiagram, free) },
    { label: rows.reelListicle, price: priceLabel(p.reelListicle, free) },
    ...(p.voiceClone > 0 ? [{ label: rows.voiceClone, price: priceLabel(p.voiceClone, free) }] : []),
  ];
}

/** A settings field: a whole number from 0 to 1 000 000, or null. */
export function parseSettingValue(raw: string): number | null {
  const s = raw.trim();
  if (!/^[0-9]{1,7}$/.test(s)) return null;
  const n = Number(s);
  return n <= 1_000_000 ? n : null;
}

export function personLabel(p: {
  username: string | null;
  firstName: string | null;
  telegramId: number;
}): string {
  return p.username ? `@${p.username}` : (p.firstName ?? `id${p.telegramId}`);
}
